import { z } from "zod";
import { oidcDenialReasonSchema } from "./oidc-denial-reason.js";
/**
 * Browser sign-in handed over by MLX's native app (decision D56, which
 * replaced D47's Clerk sign-in tokens; docs/architecture/auth.md "Browser
 * sign-in from the native app"). The iPhone app, already signed in, asks its
 * issuer for a one-time code that signs its in-app browser in to one of its
 * organisation's MLX app pages, because Google refuses sign-in inside
 * embedded web views. The code is the issuer's own: single use, bound to the
 * browser the app planted a secret in, and it creates no Clerk session.
 */
/** Path on the issuer (`https://mlx.systems/auth`, `https://auth-hpt.mlx.systems`). POST only. */
export const BROWSER_SIGN_IN_PATH = "/api/auth/mlx/browser-sign-in";
/** The Twin gateway's native sign-in callback, which the Twin CLI signs in through over SSH. */
export const TWIN_GATEWAY_NATIVE_SIGN_IN_CALLBACK_PATH = "/api/twin/cli-auth/callback";
/**
 * The Twin gateway's sign-in callback for the iPhone app, a path of its own:
 * the redirect of the iPhone app's own client, never the Twin CLI's.
 */
export const TWIN_GATEWAY_IOS_SIGN_IN_CALLBACK_PATH = "/api/twin/ios-auth/callback";
/**
 * MLX's iPhone app's own public client, which each issuer keeps itself (not
 * registered dynamically, so no one else can register it or its callback, or
 * name it): the only client a sign-in is handed to the browser for. Client ids
 * are unique across the issuers sharing a database, so each issuer's is its
 * own, `mlxc_twin_desk_ios_` and a digest of the issuer, and the issuer names
 * it in its OpenID configuration under this field; the app signs in with the
 * id it finds there rather than registering one.
 */
export const TWIN_DESK_IOS_CLIENT_DISCOVERY_FIELD = "mlx_twin_desk_ios_client_id";
export const TWIN_DESK_IOS_CLIENT_NAME = "Twin Desk for iPhone";
/** How long the code in the returned URL may be redeemed for. */
export const BROWSER_SIGN_IN_CODE_TTL_SECONDS = 60;
/**
 * The cookie the app plants in its own browser, for the issuer, before it
 * opens the returned URL: a random secret whose SHA-256 (base64url) the
 * request names as `browserBinding`. The code is redeemed only in a browser
 * that carries it.
 */
export const BROWSER_SIGN_IN_BINDING_COOKIE = "mlx_browser_binding";
export const browserSignInRequestSchema = z.object({
    /** An absolute https URL on one of the token organisation's MLX app origins. */
    returnTo: z.string().trim().min(1).max(2048),
    /** base64url SHA-256 of the secret in `BROWSER_SIGN_IN_BINDING_COOKIE`. */
    browserBinding: z.string().regex(/^[A-Za-z0-9_-]{43}$/u),
}).strict();
export const browserSignInResponseSchema = z.object({
    /**
     * The issuer's hand-over page. Its fragment carries the single-use code:
     * open it at once, once, in the browser that carries the binding cookie;
     * never log, store or share it.
     */
    url: z.string().url(),
    /** Epoch milliseconds after which the code no longer signs anyone in. */
    expiresAt: z.number().int(),
    /** The page the browser lands on once signed in, as accepted. */
    returnTo: z.string().url(),
}).strict();
export const browserSignInErrorCodeSchema = z.enum([
    "invalid_request",
    "invalid_return_to",
    "invalid_token",
    "insufficient_scope",
    "unauthorized_client",
    "access_denied",
    "too_many_requests",
    "temporarily_unavailable",
    "server_error",
]);
export const browserSignInErrorSchema = z.object({
    error: browserSignInErrorCodeSchema,
    error_description: z.string(),
    /** Present when the app's sign-in policy refuses this hand-over. */
    mlx_reason: oidcDenialReasonSchema.optional(),
});
